import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { throttle } from 'lodash';

type ScrollAction = 'hide' | 'show';

interface UseScrollDisplayProps {
  threshold?: number;
  action?: ScrollAction;
  lockStateOnThreshold?: boolean;
  scrollableElRef?: React.RefObject<HTMLElement>;
  resetOnNavigate?: boolean;
}

export function useScrollDisplay(props: UseScrollDisplayProps) {
  const {
    threshold = 50,
    action = 'hide',
    lockStateOnThreshold = false,
    scrollableElRef,
    resetOnNavigate = false,
  } = props;

  const [isVisible, setIsVisible] = useState(action === 'hide');
  const [isLocked, setIsLocked] = useState(false);
  const { pathname } = useLocation();

  // Effect to reset state on navigation
  useEffect(() => {
    if (resetOnNavigate) {
      setIsVisible(action === 'show');
      setIsLocked(false);
    }
  }, [pathname, resetOnNavigate, action]);

  // Effect to handle scroll logic
  useEffect(() => {
    if (isLocked) return;

    const scrollableEl = scrollableElRef?.current || window;

    const handleScroll = throttle(
      () => {
        const scrollY = Math.round(
          scrollableEl instanceof Window
            ? scrollableEl.scrollY
            : scrollableEl.scrollTop,
        );

        const shouldBeVisible =
          action === 'show' ? scrollY > threshold : scrollY <= threshold;

        // Lock the state only when the threshold is passed, not on any visibility change.
        if (lockStateOnThreshold && shouldBeVisible) {
          setIsLocked(true);
        }

        setIsVisible(shouldBeVisible);
      },
      100,
      { leading: true, trailing: true },
    );

    scrollableEl.addEventListener('scroll', handleScroll);

    return () => {
      scrollableEl.removeEventListener('scroll', handleScroll);
      handleScroll.cancel(); // Cancel any pending throttled calls
    };
    // The dependency array is minimal to avoid re-attaching the listener unnecessarily.
    // isLocked is the primary gatekeeper.
  }, [isLocked, action, threshold, lockStateOnThreshold, scrollableElRef]);

  return isVisible;
}
